import { combineReducers } from 'redux';
import { takeLatest } from 'redux-saga/effects';
import { throttledSaga, simpleAsyncSaga } from '../helpers/EzeeSaga';
import { EzeeAsyncAction } from '../helpers/EzeeAsyncAction';

import { Section, SimpleListResponse } from '../api/apiTypes';
import { MainReducerState, RequestState } from '../reducers';

import {
    ListPayload,
    IdPayload,
    DataPayload,
    list as listApiCall,
    create as createApiCall,
    del as deleteApiCall,
    update as updateApiCall,
    details as detailsApiCall,
} from '../api/sections';

// State

export interface SectionsState {
    list: RequestState<SimpleListResponse<Section>>;
    details: RequestState<Section>;
    create: RequestState<Section>;
    update: RequestState<Partial<Section>>;
    delete: RequestState<Section>;
}

const initialState: SectionsState = {
    list: {
        data: {
            items: [],
        },
        loading: false,
        success: false,
    },
    details: {
        data: undefined,
        loading: false,
        success: false,
    },
    create: {
        loading: false,
        success: false,
    },
    update: {
        loading: false,
        success: false,
    },
    delete: {
        loading: false,
        success: false,
    },
};

// Actions/Reducers

export const list = new EzeeAsyncAction<
    SectionsState['list'],
    ListPayload,
    SimpleListResponse<Section>
>('sections/list', initialState.list, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: false,
    }
    ),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.list,
    }),
});

export const details = new EzeeAsyncAction<
    SectionsState['details'],
    IdPayload,
    Section
>('sections/details', initialState.details, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: false,
    }
    ),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.details,
    }),
});

export const create = new EzeeAsyncAction<
    SectionsState['create'],
    DataPayload,
    Section
    >('sections/create', initialState.create, {
    trigger: (state, payload) => ({
        ...state,
        ...payload,
        loading: true,
        success: undefined,
        error: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: undefined,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.create,
    }),
});

export const update = new EzeeAsyncAction<
    SectionsState['update'],
    DataPayload,
    Partial<Section>
>('sections/update', initialState.update, {
    trigger: (state, payload) => ({
        ...state,
        ...payload,
        loading: true,
        success: undefined,
        error: undefined,
    }),
    success: (state, payload) => ({
        ...state,
        data: payload,
        loading: false,
        success: true,
        error: undefined,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.update,
    }),
});

export const del = new EzeeAsyncAction<
    SectionsState['delete'],
    IdPayload,
    Section
>('sections/delete', initialState.delete, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.delete,
    }),
});
// Reducer

export const sectionsReducer = combineReducers<SectionsState>({
    list: list.reducer,
    details: details.reducer,
    create: create.reducer,
    update: update.reducer,
    delete: del.reducer,
});

// Saga

export function* sectionsSaga() {
    yield takeLatest(list.type.trigger, throttledSaga(listApiCall, list));
    yield takeLatest(details.type.trigger, simpleAsyncSaga(detailsApiCall, details));
    yield takeLatest(create.type.trigger, simpleAsyncSaga(createApiCall, create));
    yield takeLatest(update.type.trigger, simpleAsyncSaga(updateApiCall, update));
    yield takeLatest(del.type.trigger, simpleAsyncSaga(deleteApiCall, del));
}

// Store helpers

export const getSectionsListState = (state: MainReducerState) => state.sections.list;
export const getSectionDetailsState = (state: MainReducerState) => state.sections.details;
export const getSectionCreateState = (state: MainReducerState) => state.sections.create;
export const getSectionUpdateState = (state: MainReducerState) => state.sections.update;
export const getSectionDeleteState = (state: MainReducerState) => state.sections.delete;
